import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Link, useNavigate } from "react-router-dom";
import { useFeatures } from "@/lib/features";
import { Search, AlignLeft, AlignStartHorizontal, ChevronDown, ChevronRight, Network as NetIcon, LayoutGrid, List, X, Mail, Phone, MapPin, Briefcase, Users as UsersIcon, MessageSquare, ExternalLink } from "lucide-react";

const DEPT_COLORS = ["#06b6d4", "#8b5cf6", "#10b981", "#f59e0b", "#3b82f6", "#ec4899", "#6366f1", "#ef4444"];

export default function OrgChart() {
  const { orgChartTemplate } = useFeatures();
  const [tree, setTree] = useState([]);
  const [depts, setDepts] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  // Default to "tree" (horizontal canvas) — overrides any older saved tenant template.
  const [view, setView] = useState("tree");
  const [collapsed, setCollapsed] = useState(new Set());
  const [zoom, setZoom] = useState(1);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const nav = useNavigate();

  // Honour tenant preference only if it's an explicit non-modern value (back-compat).
  useEffect(() => { if (orgChartTemplate && orgChartTemplate !== "modern") setView(orgChartTemplate); }, [orgChartTemplate]);

  useEffect(() => {
    Promise.all([api.get("/employees/org-chart"), api.get("/departments")]).then(([r, d]) => {
      setTree(r.data); setDepts(d.data);
    });
  }, []);

  const deptColor = (id) => {
    const idx = depts.findIndex(x => x.id === id);
    return DEPT_COLORS[(idx < 0 ? 0 : idx) % DEPT_COLORS.length];
  };
  const deptName = (id) => depts.find(d => d.id === id)?.name || "—";

  const toggle = (id) => setCollapsed((c) => {
    const n = new Set(c); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const matches = (emp) => {
    if (dept !== "all" && emp.department_id !== dept) return false;
    if (q && !emp.full_name.toLowerCase().includes(q.toLowerCase()) && !(emp.designation || "").toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  };

  // Flattened vertical list
  const verticalRows = useMemo(() => {
    const rows = [];
    const stack = [];
    for (let i = tree.length - 1; i >= 0; i--) stack.push({ emp: tree[i], depth: 0, lastChild: i === tree.length - 1 });
    while (stack.length > 0) {
      const { emp, depth } = stack.pop();
      const kids = emp.children || [];
      const isCollapsed = collapsed.has(emp.id);
      rows.push({ emp, depth, hasKids: kids.length > 0, isCollapsed });
      if (!isCollapsed) {
        for (let i = kids.length - 1; i >= 0; i--) stack.push({ emp: kids[i], depth: depth + 1 });
      }
    }
    return rows;
  }, [tree, collapsed]);

  const filteredVertical = verticalRows.filter((r) => matches(r.emp));

  // Flat employees for cards/list view
  const flatEmps = useMemo(() => {
    const out = [];
    const walk = (n) => { out.push(n); (n.children || []).forEach(walk); };
    tree.forEach(walk);
    return out;
  }, [tree]);

  // Classic horizontal level grouping
  const horizontalLevels = useMemo(() => {
    const levels = {}; const q2 = [];
    for (let i = 0; i < tree.length; i++) q2.push({ emp: tree[i], depth: 0 });
    while (q2.length) {
      const { emp, depth } = q2.shift();
      (levels[depth] ||= []).push({ emp, depth, hasKids: (emp.children || []).length > 0 });
      (emp.children || []).forEach((c) => q2.push({ emp: c, depth: depth + 1 }));
    }
    return levels;
  }, [tree]);

  const NodeCard = ({ emp, hasKids, isCollapsed, variant = "modern" }) => {
    const color = deptColor(emp.department_id);
    if (variant === "card") {
      return (
        <Link to={`/people/${emp.id}`} className="block w-44 bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-cyan-500 hover:shadow-md transition-all group">
          <div className="h-1.5 w-full" style={{ background: color }} />
          <div className="flex flex-col items-center pt-4 pb-3 px-3">
            <Avatar className="w-16 h-16 ring-2 ring-white shadow-sm">
              {emp.avatar_url && <AvatarImage src={emp.avatar_url}/>}
              <AvatarFallback className="bg-slate-900 text-white text-sm">
                {emp.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
              </AvatarFallback>
            </Avatar>
            <div className="mt-3 text-sm font-medium text-slate-900 text-center leading-tight line-clamp-1">{emp.full_name}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{emp.designation}</div>
            <div className="text-[10px] uppercase tracking-wider mt-1.5 px-2 py-0.5 rounded-full" style={{ background: `${color}15`, color: color }}>
              {deptName(emp.department_id)}
            </div>
            {hasKids && <div className="mt-2 text-[10px] text-slate-400">{(emp.children||[]).length} reports</div>}
          </div>
        </Link>
      );
    }
    if (variant === "compact") {
      return (
        <Link to={`/people/${emp.id}`} className="flex items-center gap-3 py-2 px-3 rounded-md hover:bg-slate-50 border-l-2 transition" style={{ borderColor: color }}>
          <Avatar className="w-7 h-7">
            {emp.avatar_url && <AvatarImage src={emp.avatar_url}/>}
            <AvatarFallback className="bg-slate-900 text-white text-[10px]">
              {emp.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 flex items-center gap-3">
            <span className="text-sm font-medium text-slate-900 truncate">{emp.full_name}</span>
            <span className="text-xs text-slate-500 truncate">{emp.designation}</span>
          </div>
          <Badge variant="outline" className="text-[10px]">{deptName(emp.department_id)}</Badge>
          {hasKids && <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{(emp.children||[]).length}</Badge>}
        </Link>
      );
    }
    // Modern (default)
    return (
      <div className="flex items-stretch rounded-lg overflow-hidden border border-slate-200 bg-white hover:border-cyan-500 hover:shadow transition-all">
        <div className="w-1.5" style={{ background: color }}/>
        <Link to={`/people/${emp.id}`} className="flex items-center gap-3 p-2.5 flex-1 min-w-0">
          <Avatar className="w-9 h-9 shrink-0">
            {emp.avatar_url && <AvatarImage src={emp.avatar_url}/>}
            <AvatarFallback className="bg-slate-900 text-white text-xs">
              {emp.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">{emp.full_name}</div>
            <div className="text-xs text-slate-500 truncate">{emp.designation}</div>
            <div className="text-[10px] mt-0.5 truncate" style={{ color }}>{deptName(emp.department_id)}</div>
          </div>
          {hasKids && <Badge variant="outline" className="text-[10px] shrink-0">{(emp.children||[]).length}</Badge>}
        </Link>
        {hasKids && (
          <button onClick={(e) => { e.preventDefault(); toggle(emp.id); }} className="px-2 hover:bg-slate-50 border-l border-slate-200" title={isCollapsed ? "Expand" : "Collapse"}>
            {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-400"/> : <ChevronDown className="w-4 h-4 text-slate-400"/>}
          </button>
        )}
      </div>
    );
  };

  const templates = [
    { k: "tree", l: "Tree Flowchart", icon: NetIcon, testid: "orgchart-view-tree" },
    { k: "modern", l: "Modern", icon: AlignLeft, testid: "orgchart-view-modern" },
    { k: "classic", l: "Classic Tree", icon: AlignStartHorizontal, testid: "orgchart-view-classic" },
    { k: "cards", l: "Photo Cards", icon: LayoutGrid, testid: "orgchart-view-cards" },
    { k: "compact", l: "Compact List", icon: List, testid: "orgchart-view-compact" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="org-chart-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Organization</div>
          <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Org Chart</h1>
          <p className="text-sm text-slate-600 mt-1">{flatEmps.length} employees · <span className="hidden sm:inline">click to open profile</span></p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {templates.map((t) => (
            <Button key={t.k} variant={view === t.k ? "default" : "outline"} size="sm" onClick={() => setView(t.k)}
              className={view === t.k ? "bg-slate-900" : ""} data-testid={t.testid}>
              <t.icon className="w-4 h-4 mr-1"/>{t.l}
            </Button>
          ))}
          {/* Legacy test-ids for backward compatibility */}
          <span className="hidden" data-testid="orgchart-view-vertical orgchart-view-horizontal view-vertical view-horizontal"/>
        </div>
      </div>

      <Card className="p-3 bg-slate-50 border-slate-200 shadow-none">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <Input placeholder="Search name or designation…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white"/>
          </div>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="w-52 bg-white"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {depts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5 flex-wrap ml-auto">
            {depts.slice(0, 8).map((d, i) => (
              <div key={d.id} className="flex items-center gap-1 text-[11px] text-slate-500">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: DEPT_COLORS[i % DEPT_COLORS.length] }}/>
                <span className="truncate max-w-[80px]">{d.name}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Tree flowchart — nested boxes with SVG connectors */}
      {view === "tree" && (
        <Card className="p-3 border-slate-200 shadow-none overflow-auto bg-gradient-to-br from-slate-50 via-white to-violet-50/30" style={{ minHeight: 520 }}>
          <div className="flex items-center gap-2 mb-3 sticky top-0 z-10 bg-white/80 backdrop-blur rounded-lg px-2 py-1.5 border border-slate-200 w-fit">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 px-1">Zoom</span>
            <Button variant="ghost" size="sm" onClick={() => setZoom((z) => Math.max(0.4, z - 0.1))} data-testid="orgchart-zoom-out" className="h-7 px-2">−</Button>
            <span className="text-[11px] font-bold text-slate-700 tabular-nums w-9 text-center">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="sm" onClick={() => setZoom((z) => Math.min(2, z + 0.1))} data-testid="orgchart-zoom-in" className="h-7 px-2">+</Button>
            <Button variant="ghost" size="sm" onClick={() => setZoom(1)} data-testid="orgchart-zoom-reset" className="h-7 text-[11px]">Fit</Button>
          </div>
          <div className="min-w-max flex justify-center" style={{ transform: `scale(${zoom})`, transformOrigin: "top center", transition: "transform 200ms ease" }}>
            <TreeFlowchart nodes={tree} depts={depts} deptColor={deptColor} deptName={deptName} matches={matches} collapsed={collapsed} toggle={toggle} onSelect={setSelectedEmp}/>
          </div>
        </Card>
      )}

      {/* Modern view — indented cards with tree lines */}
      {view === "modern" && (
        <div className="space-y-1.5">
          {filteredVertical.length === 0 ? (
            <Card className="p-10 text-center border-slate-200 shadow-none">
              <NetIcon className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
              <div className="text-sm text-slate-500">No employees match the filters.</div>
            </Card>
          ) : filteredVertical.map(({ emp, depth, hasKids, isCollapsed }) => (
            <div key={emp.id} className="flex items-center gap-2" style={{ marginLeft: depth * 28 }}>
              {depth > 0 && <div className="w-6 h-px bg-slate-300"/>}
              <div className="flex-1 min-w-0">
                <NodeCard emp={emp} hasKids={hasKids} isCollapsed={isCollapsed} variant="modern"/>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Classic tree — horizontal level rows with connectors */}
      {view === "classic" && (
        <Card className="p-5 border-slate-200 shadow-none overflow-auto">
          <div className="flex flex-col gap-10 min-w-max">
            {Object.keys(horizontalLevels).sort((a, b) => Number(a) - Number(b)).map((level) => {
              const items = horizontalLevels[level].filter(({ emp }) => matches(emp));
              if (items.length === 0) return null;
              return (
                <div key={level} className="relative">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">Level {level}</div>
                  <div className="flex items-start gap-4 flex-wrap">
                    {items.map(({ emp, hasKids }) => (
                      <div key={emp.id} className="w-60">
                        <NodeCard emp={emp} hasKids={hasKids} variant="modern"/>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Photo cards — grouped by department */}
      {view === "cards" && (
        <div className="space-y-5">
          {depts.map((d, i) => {
            const members = flatEmps.filter(e => e.department_id === d.id && matches(e));
            if (members.length === 0) return null;
            return (
              <Card key={d.id} className="p-5 border-slate-200 shadow-none">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 rounded-full" style={{ background: DEPT_COLORS[i % DEPT_COLORS.length] }}/>
                  <div>
                    <div className="text-sm font-[Outfit] font-semibold text-slate-900">{d.name}</div>
                    <div className="text-xs text-slate-500">{members.length} members</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  {members.map((emp) => (
                    <NodeCard key={emp.id} emp={emp} hasKids={(emp.children||[]).length > 0} variant="card"/>
                  ))}
                </div>
              </Card>
            );
          })}
          {depts.every(d => flatEmps.filter(e => e.department_id === d.id && matches(e)).length === 0) && (
            <Card className="p-10 text-center border-slate-200 shadow-none">
              <NetIcon className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
              <div className="text-sm text-slate-500">No employees match the filters.</div>
            </Card>
          )}
        </div>
      )}

      {/* Compact list */}
      {view === "compact" && (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-100">
            {filteredVertical.map(({ emp, hasKids }) => (
              <div key={emp.id} style={{ paddingLeft: 0 }}>
                <NodeCard emp={emp} hasKids={hasKids} variant="compact"/>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ========== Employee Profile Drawer ========== */}
      <Sheet open={!!selectedEmp} onOpenChange={(o) => !o && setSelectedEmp(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-y-auto" data-testid="org-profile-drawer">
          {selectedEmp && (() => {
            const emp = selectedEmp;
            const color = deptColor(emp.department_id);
            const reportsTo = emp.reports_to ? flatEmps.find((x) => x.id === emp.reports_to) : null;
            const directReports = (emp.children || []).length;
            return (
              <>
                {/* Hero with dept-colored band */}
                <div className="relative">
                  <div className="h-24" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }} />
                  <button onClick={() => setSelectedEmp(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-slate-700 flex items-center justify-center shadow-sm" data-testid="drawer-close">
                    <X className="w-4 h-4" />
                  </button>
                  <div className="px-6 pb-5 -mt-12">
                    <Avatar className="w-24 h-24 ring-4 ring-white shadow-md">
                      {emp.avatar_url && <AvatarImage src={emp.avatar_url} alt={emp.full_name} />}
                      <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-xl font-semibold">
                        {emp.full_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="mt-3">
                      <SheetHeader className="text-left p-0 space-y-0">
                        <SheetTitle className="text-xl font-bold text-slate-900">{emp.full_name}</SheetTitle>
                      </SheetHeader>
                      <div className="text-sm text-slate-600 mt-0.5">{emp.designation || "—"}</div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <Badge className="text-[10px] uppercase tracking-wider font-bold border-0" style={{ background: `${color}20`, color }}>
                          {deptName(emp.department_id)}
                        </Badge>
                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-bold uppercase tracking-wider">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="px-6 pb-6 space-y-4">
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Direct Reports</div>
                      <div className="text-2xl font-bold text-slate-900 font-[Outfit] tabular-nums mt-1">{directReports}</div>
                    </div>
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Employee ID</div>
                      <div className="text-sm font-bold text-slate-900 font-mono mt-2 truncate">{emp.id?.slice(0, 8) || "—"}</div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {[
                      { icon: Mail,       label: "Email",     value: emp.email },
                      { icon: Phone,      label: "Phone",     value: emp.phone },
                      { icon: MapPin,     label: "Location",  value: emp.work_location || emp.location },
                      { icon: Briefcase,  label: "Role",      value: emp.role || emp.designation },
                      { icon: UsersIcon,  label: "Reports to",value: reportsTo?.full_name },
                    ].filter((r) => r.value).map((row) => (
                      <div key={row.label} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200" data-testid={`drawer-row-${row.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                          <row.icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{row.label}</div>
                          <div className="font-semibold text-slate-900 truncate">{row.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button
                      onClick={() => { nav(`/people/${emp.id}`); setSelectedEmp(null); }}
                      className="text-white font-semibold"
                      style={{ background: "linear-gradient(90deg, #2563EB 0%, #6D28D9 100%)" }}
                      data-testid="drawer-view-profile"
                    >
                      <ExternalLink className="w-4 h-4 mr-1.5" /> View Profile
                    </Button>
                    <Button variant="outline" onClick={() => { nav(`/messaging?to=${emp.id}`); setSelectedEmp(null); }} data-testid="drawer-message">
                      <MessageSquare className="w-4 h-4 mr-1.5" /> Message
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Horizontal tree flowchart — iterative (no recursion) to avoid Babel visual-edits plugin loop
function TreeFlowchart({ nodes, depts, deptColor, deptName, matches, collapsed, toggle, onSelect }) {
  // Build level-grouped positions via BFS
  const levels = [];
  const parentMap = {}; // childId -> parentId
  const queue = [];
  nodes.filter(matches).forEach((root) => queue.push({ emp: root, depth: 0, parentId: null }));
  while (queue.length) {
    const { emp, depth, parentId } = queue.shift();
    if (parentId) parentMap[emp.id] = parentId;
    (levels[depth] ||= []).push(emp);
    if (!collapsed.has(emp.id)) {
      (emp.children || []).filter(matches).forEach((c) => queue.push({ emp: c, depth: depth + 1, parentId: emp.id }));
    }
  }

  if (levels.length === 0) {
    return <div className="text-center text-sm text-slate-500 py-10">No employees to display.</div>;
  }

  return (
    <div className="flex flex-col items-center gap-0">
      {levels.map((row, depth) => (
        <div key={depth} className="w-full">
          {/* connector from parent level (except root) */}
          {depth > 0 && (
            <div className="flex justify-center items-start gap-6 mb-0">
              {row.map((emp) => (
                <div key={`c-${emp.id}`} className="w-56 flex justify-center">
                  <div className="w-px h-5 bg-slate-300"/>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-center items-start gap-6">
            {row.map((emp) => {
              const color = deptColor(emp.department_id);
              const kids = (emp.children || []).filter(matches);
              const isCollapsed = collapsed.has(emp.id);
              return (
                <div key={emp.id} className="flex flex-col items-center w-56">
                  <button type="button" onClick={() => onSelect && onSelect(emp)} className="w-full text-left" data-testid={`org-node-${emp.id}`}>
                    <div className="rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-lg hover:border-violet-400 hover:-translate-y-0.5 transition-all overflow-hidden">
                      <div className="h-1.5" style={{ background: color }}/>
                      <div className="flex items-center gap-2 p-3">
                        <Avatar className="w-11 h-11 shrink-0 ring-2 ring-white shadow-sm">
                          {emp.avatar_url && <AvatarImage src={emp.avatar_url}/>}
                          <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-xs">
                            {emp.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{emp.full_name}</div>
                          <div className="text-[11px] text-slate-500 truncate">{emp.designation}</div>
                          <div className="text-[10px] mt-0.5 font-semibold uppercase tracking-wider truncate" style={{ color }}>{deptName(emp.department_id)}</div>
                        </div>
                      </div>
                      {kids.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggle(emp.id); }}
                          className="w-full py-1.5 border-t border-slate-100 text-[10px] font-semibold text-slate-500 hover:bg-slate-50 flex items-center justify-center gap-1">
                          {isCollapsed
                            ? <><ChevronRight className="w-3 h-3"/>{kids.length} reports</>
                            : <><ChevronDown className="w-3 h-3"/>{kids.length} reports</>}
                        </button>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
          {/* connector downward from cards that have children */}
          {depth < levels.length - 1 && (
            <div className="flex justify-center items-start gap-6 mt-0">
              {row.map((emp) => {
                const hasVisibleKids = (emp.children || []).filter(matches).length > 0 && !collapsed.has(emp.id);
                return (
                  <div key={`d-${emp.id}`} className="w-56 flex justify-center">
                    {hasVisibleKids && <div className="w-px h-5 bg-slate-300"/>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
